/**
 * Clinic boundary contract — signup, membership, invitations, episode grants,
 * and the two clinic-home reports (increment-12 plan §2.P1).
 *
 * The clinic is the boundary every other record now sits inside (ADR-0027):
 * a clinician sees a record because a MEMBERSHIP of the clinic that holds it
 * says so, and an episode inside that clinic is reachable through an EPISODE
 * ROLE — owner, covering, read-only (ADR-0028). The clinic itself is signed up
 * on the web and only becomes usable when an operator approves it
 * (ADR-0030 §2–§3), and the terms it agrees to at signup are the processing
 * agreement between the clinic (the data fiduciary) and the platform
 * (ADR-0029).
 *
 * TWO CONVENTIONS RUN THROUGH THIS FILE, both inherited rather than invented:
 *
 *  - **Requests use `.optional()`, read shapes use `.nullable()`** — a client
 *    omits a field it has nothing to say about, while a row read out of
 *    Postgres carries an explicit `null` (episode.ts's read shapes are the
 *    precedent). `clinicProfileSchema` is the one schema travelling in both
 *    directions, so its one optional field is `.nullish()`.
 *  - **The console renders; the API decides** (plan §0 rule 7). Nothing here
 *    is a permission check. These are shapes — the scoping that decides who
 *    may see a roster row lives in the API, and a schema that looked like a
 *    guard would invite a client to enforce it.
 *
 * NO SCHEMA HERE HAS A BACKFILL STORY (plan §0 rule 6): rows that predate the
 * clinic tables belong to no clinic and stay unreachable. There is deliberately
 * no "legacy" or "unassigned" clinic id in this vocabulary.
 */

import { z } from 'zod';

import { programDayPositionSchema } from './episode';

// ---- vocabularies ----------------------------------------------------------

/**
 * Terms versions a clinic can agree to. Append-only, exactly as
 * `CONSENT_TEXT_VERSIONS` is: an agreement recorded under a version must keep
 * resolving to it forever, so a wording change is a NEW version and never an
 * edit of an old one (ADR-0029 §4's version-lock, applied to the clinic side).
 */
export const CLINIC_TERMS_VERSIONS = ['AN-CLINIC-TERMS@1.0'] as const;
export type ClinicTermsVersion = (typeof CLINIC_TERMS_VERSIONS)[number];

/** The terms version every new signup agrees to. */
export const CURRENT_CLINIC_TERMS_VERSION: ClinicTermsVersion = 'AN-CLINIC-TERMS@1.0';

/**
 * What a person may be inside a clinic. `clinician` treats patients; `admin`
 * manages the clinic's people and its profile. They are independent, not a
 * ladder — an admin who also treats holds both, and an office manager who
 * treats nobody holds `admin` alone.
 */
export const CLINIC_ROLES = ['clinician', 'admin'] as const;
export type ClinicRole = (typeof CLINIC_ROLES)[number];

/**
 * What a clinician may be on ONE episode (ADR-0028 §2). `owner` is the
 * clinician responsible for the episode; `covering` is a time-boxed stand-in;
 * `read-only` can see the record and write nothing.
 */
export const EPISODE_ROLES = ['owner', 'covering', 'read-only'] as const;
export type EpisodeRole = (typeof EPISODE_ROLES)[number];

/**
 * A clinic is `pending` from signup until an operator approves it, and
 * `approved` after (ADR-0030 §3). Approval is the existence of an approval
 * row, never a mutable status column — so this vocabulary is a READ shape,
 * derived by the API, and no client ever sends it.
 */
export const CLINIC_STATUSES = ['pending', 'approved'] as const;
export type ClinicStatus = (typeof CLINIC_STATUSES)[number];

// ---- field-level building blocks -------------------------------------------

/**
 * The authenticated identity a membership or grant names — the verified `sub`
 * of the identity provider, which is the only actor value the platform ever
 * stores (§0.2, `actorOf`). Not `z.uuid()` on purpose: the sub's shape is the
 * provider's business, and a format assumption here would be a second reading
 * of a fact the API already holds.
 */
const subSchema = z.string().trim().min(1).max(200);

/**
 * Why a membership, grant or revocation was recorded. Required and non-trivial
 * everywhere it appears: these rows are the audit answer to "who could see
 * this patient, and on whose say-so" (ADR-0028 §4), and an empty reason makes
 * that answer useless.
 */
const reasonSchema = z.string().trim().min(3).max(500);

/**
 * A free-text phone or emergency number, kept deliberately loose. Clinics
 * write `+91 22 1234 5678`, `022-12345678` and `9820012345` and all three are
 * the same usable number; a format the platform invented would refuse real
 * clinics at signup and buy nothing (nothing dials these — a human reads them).
 */
const phoneSchema = z.string().trim().min(4).max(40);

/**
 * Trim BEFORE the format check, which is why this is a pipe: a pasted address
 * arrives with whitespace, and `z.email().trim()` would refuse it before the
 * trim ever ran.
 */
const emailSchema = z.string().trim().pipe(z.email().max(200));

/**
 * The roles a membership carries. Non-empty (a membership that grants nothing
 * is not a membership) and duplicate-free, mirroring migration 022's
 * `roles text[]` CHECK so the wire cannot carry a value the table refuses.
 */
export const clinicRolesSchema = z
  .array(z.enum(CLINIC_ROLES))
  .min(1)
  .max(CLINIC_ROLES.length)
  .refine((roles) => new Set(roles).size === roles.length, {
    message: 'roles must not repeat',
  });
export type ClinicRoles = z.infer<typeof clinicRolesSchema>;

// ---- the clinic profile ----------------------------------------------------

/**
 * The clinic as it describes itself. This is the head of a superseding chain
 * (migration 022: a profile edit is a NEW row), and it is also what the
 * patient consent form prints as the responsible party — which is why the
 * contact fields are required rather than nice-to-have (ADR-0029 §2: the
 * clinic is the data fiduciary and the patient must be able to reach it).
 *
 * `registrationRef` is the clinic's own registration number where it has one.
 * It is `.nullish()` because this single schema travels both ways: omitted on
 * the way in, `null` on the way back out of Postgres.
 */
export const clinicProfileSchema = z.object({
  name: z.string().trim().min(2).max(200),
  address: z.string().trim().min(5).max(500),
  registrationRef: z.string().trim().min(1).max(120).nullish(),
  contactName: z.string().trim().min(2).max(200),
  contactPhone: phoneSchema,
  contactEmail: emailSchema,
  /** The number a patient calls when something is wrong outside a session. */
  emergencyNumber: phoneSchema,
});
export type ClinicProfile = z.infer<typeof clinicProfileSchema>;

/**
 * POST /api/clinics — a clinic signing itself up (ADR-0030 §2).
 *
 * `agree` is `z.literal(true)`, not a boolean: agreement is the whole legal
 * event this request records, so a request that carries `false` is not a
 * signup with a flag cleared, it is a malformed signup. The version travels
 * WITH the agreement because what was agreed to is a historical fact — the
 * server records the version it was sent, never "the current one" at read
 * time.
 */
export const clinicSignupRequestSchema = z.object({
  profile: clinicProfileSchema,
  termsVersion: z.enum(CLINIC_TERMS_VERSIONS),
  agree: z.literal(true),
});
export type ClinicSignupRequest = z.infer<typeof clinicSignupRequestSchema>;

// ---- "which clinic am I in?" ----------------------------------------------

/** The clinic head as every console screen names it. */
export const clinicHeadSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  status: z.enum(CLINIC_STATUSES),
});
export type ClinicHead = z.infer<typeof clinicHeadSchema>;

/**
 * GET /api/clinics/mine — the console's first call after sign-in, and the
 * only branch point in its whole routing.
 *
 * `{ clinic: null }` is a NORMAL answer, not an error: a signed-in person who
 * belongs to no clinic yet is exactly who the signup and invitation-claim
 * screens are for (plan §0 rule 6 — nothing is adopted for them). A refusal
 * (`no_clinic`) is what the clinic-scoped routes answer; this route answers
 * the question instead.
 *
 * A `pending` clinic still comes back in full — the console must be able to
 * say "we have your details, an operator is reviewing them" and print the
 * profile, which it cannot do from a refusal.
 */
export const clinicMineResponseSchema = z.union([
  z.object({
    ok: z.literal(true),
    clinic: clinicHeadSchema,
    /** The profile head — what the clinic looks like right now. */
    profile: clinicProfileSchema,
    /** The caller's own roles in this clinic. */
    roles: clinicRolesSchema,
    /** When the caller's ACTIVE membership started (not when the clinic did). */
    membershipSince: z.iso.datetime(),
  }),
  z.object({
    ok: z.literal(true),
    clinic: z.null(),
  }),
]);
export type ClinicMineResponse = z.infer<typeof clinicMineResponseSchema>;

// ---- memberships -----------------------------------------------------------

/**
 * One person in the clinic's people list. `endedAt` non-null means the
 * membership is over and the row is history — memberships are never deleted
 * (append-only), so an ended one keeps explaining past access.
 *
 * `displayName` is nullable because the platform does not own it: it comes
 * from the identity provider, and a member who never set one is shown by the
 * console as their sign-in address rather than as a blank.
 */
export const membershipRowSchema = z.object({
  id: z.uuid(),
  memberSub: subSchema,
  displayName: z.string().min(1).max(200).nullable(),
  roles: clinicRolesSchema,
  startedAt: z.iso.datetime(),
  endedAt: z.iso.datetime().nullable(),
});
export type MembershipRow = z.infer<typeof membershipRowSchema>;

/** GET /api/clinics/:clinicId/members */
export const membershipListResponseSchema = z.object({
  ok: z.literal(true),
  members: z.array(membershipRowSchema),
});
export type MembershipListResponse = z.infer<typeof membershipListResponseSchema>;

/**
 * POST /api/clinics/:clinicId/memberships — an admin changing one person's
 * roles, or ending their membership.
 *
 * Exactly one of `roles` and `end` is sent. Both together is refused rather
 * than resolved by precedence: "change their roles AND remove them" has two
 * plausible readings and the caller knows which it meant, so the request comes
 * back for the caller to say. Neither is refused for the same reason.
 *
 * `end: z.literal(true)` — there is no `end: false` meaning "reinstate"; a
 * person coming back is a new membership row with its own reason.
 */
export const membershipChangeRequestSchema = z
  .object({
    memberSub: subSchema,
    roles: clinicRolesSchema.optional(),
    end: z.literal(true).optional(),
    reason: reasonSchema,
  })
  .refine((body) => (body.roles === undefined) !== (body.end === undefined), {
    message: 'send exactly one of roles or end',
    path: ['roles'],
  });
export type MembershipChangeRequest = z.infer<typeof membershipChangeRequestSchema>;

// ---- invitations -----------------------------------------------------------

/**
 * Hours an invitation stays claimable, capped at a week (168). The cap is the
 * point: an invitation is a bearer credential to a clinic's whole patient
 * list, so it expires on its own even when everybody forgets it. Required, not
 * defaulted — the admin who mints it says how long it lives.
 */
const expiresInHoursSchema = z.number().int().min(1).max(168);

/** POST /api/clinics/:clinicId/invitations */
export const invitationCreateRequestSchema = z.object({
  roles: clinicRolesSchema,
  expiresInHours: expiresInHoursSchema,
});
export type InvitationCreateRequest = z.infer<typeof invitationCreateRequestSchema>;

/**
 * The response to minting an invitation.
 *
 * `token` IS RETURNED EXACTLY ONCE, here, and is never readable again: the
 * table stores only its hash (migration 022's `token_hash`), so a leaked
 * database does not hand over a working invitation and no later screen can
 * re-display one. An admin who loses it mints another and lets the first
 * expire.
 */
export const invitationCreatedResponseSchema = z.object({
  ok: z.literal(true),
  invitationId: z.uuid(),
  /** Shown once. Not stored in clear, not re-servable. */
  token: z.string().min(1).max(200),
  expiresAt: z.iso.datetime(),
});
export type InvitationCreatedResponse = z.infer<typeof invitationCreatedResponseSchema>;

/**
 * An invitation as the admin's list shows it — deliberately WITHOUT the token
 * (see above). `claimedAt` non-null means it is spent.
 */
export const invitationRowSchema = z.object({
  id: z.uuid(),
  roles: clinicRolesSchema,
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  claimedAt: z.iso.datetime().nullable(),
});
export type InvitationRow = z.infer<typeof invitationRowSchema>;

/** GET /api/clinics/:clinicId/invitations */
export const invitationListResponseSchema = z.object({
  ok: z.literal(true),
  invitations: z.array(invitationRowSchema),
});
export type InvitationListResponse = z.infer<typeof invitationListResponseSchema>;

/**
 * POST /api/invitations/claim — a signed-in clinician joining a clinic.
 *
 * The token is bounded but otherwise unvalidated: a token that does not
 * resolve is `invite_invalid`, one whose window has passed is `invite_expired`
 * and one already spent is `invite_claimed`. Those three answers tell the
 * person what to do; `invalid_request` for a typo would not.
 */
export const invitationClaimRequestSchema = z.object({
  token: z.string().trim().min(1).max(200),
});
export type InvitationClaimRequest = z.infer<typeof invitationClaimRequestSchema>;

// ---- episode grants (cover) ------------------------------------------------

/**
 * POST /api/episodes/:episodeId/grants — putting a colleague on an episode
 * (ADR-0028 §2).
 *
 * `validUntil` is REQUIRED for `covering` and `read-only` and optional only
 * for `owner`, which mirrors migration 023's
 * `CHECK (role = 'owner' OR valid_until IS NOT NULL)`. Cover is a stand-in
 * while someone is away; a cover with no end is a second owner nobody
 * remembers granting. Ownership, by contrast, is the standing responsibility
 * and ends by being handed on.
 */
export const grantRequestSchema = z
  .object({
    granteeSub: subSchema,
    role: z.enum(EPISODE_ROLES),
    validUntil: z.iso.datetime().optional(),
    reason: reasonSchema,
  })
  .refine((body) => body.role === 'owner' || body.validUntil !== undefined, {
    message: 'validUntil is required unless the role is owner',
    path: ['validUntil'],
  });
export type GrantRequest = z.infer<typeof grantRequestSchema>;

/**
 * POST /api/episodes/:episodeId/grants/:grantId/revoke — ending a grant early.
 * A revocation is a superseding row with its own reason, never a delete.
 */
export const revokeRequestSchema = z.object({
  reason: reasonSchema,
});
export type RevokeRequest = z.infer<typeof revokeRequestSchema>;

/**
 * Why a whole caseload moved. A closed vocabulary, unlike every other reason
 * in this file, because this one act is the clinic's answer to an auditor
 * asking why one clinician's patients all changed hands on one day —
 * `offboarding` (they left) and `caseload` (workload rebalancing) are the two
 * legitimate answers, and free text there would be noise.
 */
export const REASSIGN_REASONS = ['offboarding', 'caseload'] as const;
export type ReassignReason = (typeof REASSIGN_REASONS)[number];

/**
 * POST /api/clinics/:clinicId/reassign-all — hand every episode owned by one
 * clinician to another (ADR-0028 §4's offboarding path).
 *
 * `fromSub` and `toSub` must differ: reassigning a caseload to its current
 * owner writes a pile of grant rows that change nothing, which is exactly the
 * kind of noise an append-only table can never be cleaned of afterwards.
 */
export const reassignAllRequestSchema = z
  .object({
    fromSub: subSchema,
    toSub: subSchema,
    reason: z.enum(REASSIGN_REASONS),
  })
  .refine((body) => body.fromSub !== body.toSub, {
    message: 'fromSub and toSub must be different people',
    path: ['toSub'],
  });
export type ReassignAllRequest = z.infer<typeof reassignAllRequestSchema>;

// ---- reports v1: roster and adherence --------------------------------------

/**
 * One person currently covering an episode, as the roster row lists them.
 *
 * ⚠ `grantId` IS THE HEAD ROW'S ID, NOT THE CHAIN'S FIRST ROW. `episode_grants`
 * is append-only with a supersedes chain (migration 023), and
 * `POST /api/episodes/:episodeId/grants/:grantId/revoke` resolves its target
 * through `liveGrantById`, which matches only a row **nothing supersedes** and
 * whose window is open. So the id that revokes a cover is the id of the row
 * that currently stands — the same value `POST …/grants` answered with when the
 * cover was granted. A chain root would parse as a uuid, look right in a
 * response, and answer `404 not_found` the first time anyone pressed Revoke.
 *
 * It is on the roster because the roster is the ONLY place the console ever
 * sees a cover (plan §2.P7): without it a Revoke control has nothing to pass.
 */
export const rosterCoverSchema = z.object({
  /** The live `episode_grants` row this cover is — the revoke route's `:grantId`. */
  grantId: z.uuid(),
  granteeSub: subSchema,
  displayName: z.string().min(1).max(200).nullable(),
  role: z.enum(EPISODE_ROLES),
  /** Non-null for `covering` and `read-only`; only ownership is open-ended. */
  validUntil: z.iso.datetime().nullable(),
});
export type RosterCover = z.infer<typeof rosterCoverSchema>;

/**
 * One line of the clinic roster (ADR-0030 §5 reports v1: WHO is looking after
 * WHOM, and where in the programme they are). Clinical detail — scores,
 * charts, flags — deliberately stays in the phone app (owner ruling, plan
 * §0.1); this is the management view.
 *
 * `programmeDay` is nullable on episode.ts's own precedent
 * (`patientListSummarySchema.programDay`): an episode with no dated calendar
 * has no position, and the roster says so rather than inventing day 1.
 */
export const rosterEntrySchema = z.object({
  episodeId: z.uuid(),
  patientName: z.string().min(1).max(200),
  /** The study code where the episode has one; not every episode is in a study. */
  studyCode: z.string().min(1).max(64).nullable(),
  ownerSub: subSchema,
  ownerDisplayName: z.string().min(1).max(200).nullable(),
  programmeDay: z
    .object({
      day: z.number().int().min(1),
      /** The same three words the phone app's day pill uses (episode.ts). */
      state: programDayPositionSchema.shape.state,
    })
    .nullable(),
  /**
   * Whether `ownerSub` currently holds an ACTIVE membership of this clinic —
   * the head of their membership chain with no `ended_at` (ADR-0027 §2), the
   * same fact `roleFor`'s clause (i) reads on every request. `false` is the
   * stranding ADR-0028 §7 was written to close: the owner of record has left,
   * so `roleFor` answers `none` for them AND for every colleague (a live cover
   * excepted) until an admin transfers or reassigns the episode. The API
   * derives it set-wise from the clinic's active membership heads (audit
   * AUD-17); the console marks the line and decides nothing (plan §0 rule 7).
   * `ownerSub` still names the true owner of record — the marker qualifies
   * the name, it never replaces it.
   */
  ownerActive: z.boolean(),
  /**
   * The episode's live grants apart from the ownership one `ownerSub` already
   * names — newest first, each carrying the `grantId` that withdraws it. Empty
   * is the normal state of an episode.
   */
  covers: z.array(rosterCoverSchema),
});
export type RosterEntry = z.infer<typeof rosterEntrySchema>;

/**
 * GET /api/clinics/:clinicId/roster
 *
 * `strandedCount` is ADR-0028 §7's guard — *the count of episodes whose current
 * owner is not an active member* — counted by the API over exactly the
 * `entries` it returns (`ownerActive === false`), so an admin's is the
 * clinic's whole number and a clinician's is the number among the lines they
 * can see. §7 put the number on the operator's ops surface; that surface does
 * not exist yet (ADR-0030's context), so it sits on the roster the clinic's
 * own admin already reads (audit AUD-17). Non-zero is a defect to act on —
 * reassign-all or a transfer clears it — not a curiosity. The console prints
 * the number it is handed and never re-counts (plan §0 rule 7).
 */
export const rosterResponseSchema = z.object({
  ok: z.literal(true),
  clinicId: z.uuid(),
  entries: z.array(rosterEntrySchema),
  strandedCount: z.number().int().min(0),
});
export type RosterResponse = z.infer<typeof rosterResponseSchema>;

/**
 * One episode's adherence, counted over its DATED slots: `done` has a session,
 * `missed` is a past slot with none, `upcoming` has not arrived yet, and
 * `total` is the slot count the three are drawn from. The API counts them
 * (plan §0 rule 7) and the console prints them; nothing here is re-derived
 * client-side.
 *
 * The three parts are NOT enforced to sum to `total` by this schema. The API
 * owns what a slot counts as — a session in progress today is the obvious
 * case — and a client-side invariant that guessed wrong would refuse a true
 * report.
 */
export const adherenceSummarySchema = z.object({
  episodeId: z.uuid(),
  done: z.number().int().min(0),
  missed: z.number().int().min(0),
  upcoming: z.number().int().min(0),
  total: z.number().int().min(0),
  /** Null until the first session happens. */
  lastSessionAt: z.iso.datetime().nullable(),
});
export type AdherenceSummary = z.infer<typeof adherenceSummarySchema>;

/** GET /api/clinics/:clinicId/adherence */
export const adherenceResponseSchema = z.object({
  ok: z.literal(true),
  clinicId: z.uuid(),
  /** The server's "today" (IST) the missed counts were derived against. */
  derivedAgainstDate: z.iso.date(),
  summaries: z.array(adherenceSummarySchema),
});
export type AdherenceResponse = z.infer<typeof adherenceResponseSchema>;

// ---- the consent form's bracket map ----------------------------------------

/**
 * What the console fills into the printable patient consent form's brackets
 * (§2.P7). The accepted consent text names the clinic as the responsible
 * party (ADR-0029 §2); these six fields are those brackets and nothing else.
 * Same field rules as the profile they come from, restated here as a NARROWER
 * shape rather than reused wholesale so that adding a profile field never
 * silently changes what gets printed on a patient's form.
 */
export const consentPrintFieldsSchema = z.object({
  clinicName: z.string().trim().min(2).max(200),
  address: z.string().trim().min(5).max(500),
  contactName: z.string().trim().min(2).max(200),
  contactPhone: phoneSchema,
  contactEmail: emailSchema,
  emergencyNumber: phoneSchema,
});
export type ConsentPrintFields = z.infer<typeof consentPrintFieldsSchema>;

/** The profile head, rendered into the consent form's brackets. */
export function consentPrintFieldsFromProfile(profile: ClinicProfile): ConsentPrintFields {
  return {
    clinicName: profile.name,
    address: profile.address,
    contactName: profile.contactName,
    contactPhone: profile.contactPhone,
    contactEmail: profile.contactEmail,
    emergencyNumber: profile.emergencyNumber,
  };
}

// ---- ops: approving a signed-up clinic -------------------------------------
//
// The lightweight stand-in for ADR-0025's full operator console: one clinician
// account on an email allow-list (`OPS_EMAILS`) reaches `/api/ops/*` instead
// of a shell running `approve-clinic.js` by hand. The row it writes is the
// SAME row the CLI writes (apps/api's services/clinicApproval.ts is the one
// place that inserts into `clinic_approvals`) — this is a new front end on an
// unchanged act, not a new kind of approval.

/** One clinic still waiting on `GET /api/ops/clinics/pending`. */
export const opsPendingClinicSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(200),
  address: z.string().min(1).max(500),
  contactName: z.string().min(1).max(200),
  contactEmail: emailSchema,
  contactPhone: phoneSchema,
  createdAt: z.iso.datetime(),
  /** Null means the clinic agreed to no terms — worth a warning before approving. */
  agreementVersion: z.string().min(1).max(50).nullable(),
});
export type OpsPendingClinic = z.infer<typeof opsPendingClinicSchema>;

/** GET /api/ops/clinics/pending */
export const opsPendingClinicsResponseSchema = z.object({
  ok: z.literal(true),
  clinics: z.array(opsPendingClinicSchema),
});
export type OpsPendingClinicsResponse = z.infer<typeof opsPendingClinicsResponseSchema>;

/** POST /api/ops/clinics/:clinicId/approve */
export const opsApproveRequestSchema = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});
export type OpsApproveRequest = z.infer<typeof opsApproveRequestSchema>;

export const opsApproveResponseSchema = z.object({
  ok: z.literal(true),
  clinicId: z.uuid(),
  approvedBy: z.string().min(1),
  approvedAt: z.iso.datetime(),
});
export type OpsApproveResponse = z.infer<typeof opsApproveResponseSchema>;
